import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Loader2, PartyPopper, PhoneCall, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const INVESTOR_TYPES = [
  "Individual Investor (₹5K-₹5L)",
  "HNI / Angel Investor (₹5L+)",
  "Franchise Brand Owner looking for Funding",
  "Franchise Operator / Partner",
];

const BUDGETS = [
  "₹5,000 - ₹25,000",
  "₹25,000 - ₹1,00,000",
  "₹1,00,000 - ₹5,00,000",
  "₹5,00,000 - ₹25,00,000",
  "₹25,00,000+",
];

const SECTORS = ["F&B / Cafes", "Gyms & Wellness", "Retail Stores", "Automotive Services", "Cloud Kitchens"];

const initialForm = {
  fullName: "",
  phone: "",
  email: "",
  city: "",
  investorType: "",
  investmentBudget: "",
  spamAnswer: "",
};

export default function LeadForm() {
  const [form, setForm] = useState(initialForm);
  const [sectors, setSectors] = useState([]);
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);
  const [{ a, b }, setNums] = useState(() => ({
    a: 2 + Math.floor(Math.random() * 8),
    b: 2 + Math.floor(Math.random() * 8),
  }));
  const [renderedAt, setRenderedAt] = useState(() => Date.now());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleSector = (s) =>
    setSectors((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const reset = () => {
    setForm(initialForm);
    setSectors([]);
    setHoneypot("");
    setReference(null);
    setRenderedAt(Date.now());
    setNums({ a: 2 + Math.floor(Math.random() * 8), b: 2 + Math.floor(Math.random() * 8) });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        ...form,
        preferredSectors: sectors,
        numA: a,
        numB: b,
        spamAnswer: parseInt(form.spamAnswer, 10),
        website: honeypot,
        renderedAt,
      });
      setReference(data.reference);
      toast.success("Expression of interest submitted successfully.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(msg);
      setNums({ a: 2 + Math.floor(Math.random() * 8), b: 2 + Math.floor(Math.random() * 8) });
      setForm((f) => ({ ...f, spamAnswer: "" }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead-form" data-testid="lead-capture-form-section" className="hero-glow py-24">
      <div className="mx-auto grid max-w-6xl items-start gap-12 px-4 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="lg:sticky lg:top-28">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Priority allocation</p>
          <h2 className="font-heading mt-4 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Join the fraction waitlist before the first outlets open
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Tell us a little about yourself. Your details go straight to the fraction by frantiger
            team inbox, and our franchise experts will call you back with early-access opportunities
            matched to your budget and city.
          </p>
          <div className="mt-8 space-y-3">
            <a href="tel:+919548444445" data-testid="contact-call-link" className="clay-card flex items-center gap-3 rounded-2xl px-5 py-4 text-sm font-bold text-slate-800 transition-transform hover:-translate-y-0.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-100 text-orange-600"><PhoneCall className="h-4 w-4" /></span>
              +91 95484 44445
            </a>
            <a href="mailto:frantiger.consulting@gmail.com" data-testid="contact-email-link" className="clay-card flex items-center gap-3 rounded-2xl px-5 py-4 text-sm font-bold text-slate-800 transition-transform hover:-translate-y-0.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-100 text-orange-600"><Mail className="h-4 w-4" /></span>
              frantiger.consulting@gmail.com
            </a>
          </div>
          <div className="mt-8 flex items-center gap-2 text-xs font-semibold text-slate-500">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            Protected by honeypot + security-sum bot checks. No spam, ever.
          </div>
        </div>

        <div className="glass-surface rounded-[2rem] p-7 shadow-2xl shadow-orange-500/10 sm:p-9" data-testid="lead-form-card">
          {reference ? (
            <div className="flex flex-col items-center py-10 text-center" data-testid="lead-success-panel">
              <span className="clay-btn flex h-20 w-20 items-center justify-center rounded-full bg-orange-500 text-white">
                <PartyPopper className="h-9 w-9" />
              </span>
              <h3 className="font-heading mt-6 text-2xl font-extrabold text-slate-900">You're on the list!</h3>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                Your expression of interest has been emailed to the fraction by frantiger team.
                Expect a call from our franchise experts within 1–2 working days.
              </p>
              <div className="neu-inset mt-6 rounded-2xl px-6 py-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Priority reference</p>
                <p className="font-heading text-xl font-black text-orange-600" data-testid="lead-reference-code">{reference}</p>
              </div>
              <button
                onClick={reset}
                data-testid="lead-submit-another-btn"
                className="clay-btn-dark mt-8 rounded-full bg-white px-6 py-3 text-sm font-bold text-slate-800"
              >
                Submit another response
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="lead-capture-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-700">Full Name *</label>
                  <input required data-testid="input-fullname" className="field-input" placeholder="e.g. Rahul Sharma" value={form.fullName} onChange={set("fullName")} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-700">WhatsApp / Phone *</label>
                  <input required data-testid="input-phone" className="field-input" placeholder="+91 98765 43210" value={form.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-700">Email *</label>
                  <input required type="email" data-testid="input-email" className="field-input" placeholder="rahul@company.com" value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-700">Your City *</label>
                  <input required data-testid="input-city" className="field-input" placeholder="e.g. Bengaluru, Mumbai, Delhi" value={form.city} onChange={set("city")} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-700">I am an... *</label>
                  <select required data-testid="select-investortype" className="field-input" value={form.investorType} onChange={set("investorType")}>
                    <option value="" disabled>Select your profile</option>
                    {INVESTOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-700">Planned Ticket Size *</label>
                  <select required data-testid="select-budget" className="field-input" value={form.investmentBudget} onChange={set("investmentBudget")}>
                    <option value="" disabled>Select budget range</option>
                    {BUDGETS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">Preferred Sectors (optional)</label>
                <div className="flex flex-wrap gap-2" data-testid="checkbox-sectors">
                  {SECTORS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`sector-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => toggleSector(s)}
                      className={`rounded-full px-4 py-2 text-xs font-bold transition-all ${
                        sectors.includes(s) ? "clay-btn bg-orange-500 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-orange-300"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="hidden"
                aria-hidden="true"
                data-testid="honeypot-field"
              />

              <div className="neu-inset flex flex-wrap items-center gap-4 rounded-2xl px-5 py-4">
                <label className="text-sm font-bold text-slate-700">
                  Security check: what is {a} + {b}? *
                </label>
                <input
                  required
                  inputMode="numeric"
                  data-testid="input-spam-check"
                  className="field-input w-24 text-center font-bold"
                  placeholder="?"
                  value={form.spamAnswer}
                  onChange={set("spamAnswer")}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="btn-submit-expression"
                className="clay-btn flex w-full items-center justify-center gap-2 rounded-full bg-orange-500 px-6 py-4 text-base font-bold text-white disabled:opacity-60"
              >
                {loading && <Loader2 className="h-5 w-5 animate-spin" />}
                {loading ? "Sending securely..." : "Submit Expression of Interest"}
              </button>
              <p className="text-center text-[10px] leading-relaxed text-slate-400">
                By submitting, you agree to be contacted by the frantiger team. This is an expression
                of interest only — not an offer, solicitation or invitation to invest.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
